import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function OrganizationLogin() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/organization/login`, formData);
      
      const orgData = {
        ...response.data.organization,
        role: 'organization'
      };
      
      login(response.data.token, orgData);
      toast.success('Login successful!');
      navigate('/organization');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-heading font-bold text-foreground">EXCEL ERA</span>
            </Link>
            <Link to="/organization/register">
              <Button variant="outline" className="font-bold uppercase text-sm">
                Register Organization
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Organization Portal</h1>
            <p className="text-muted-foreground">Manage your vehicle fleet</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Email</Label>
              <Input
                type="email"
                required
                data-testid="org-login-email"
                className="input-field mt-2"
                placeholder="org@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Password</Label>
              <Input
                type="password"
                required
                data-testid="org-login-password"
                className="input-field mt-2"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="org-login-btn"
              className="btn-primary w-full h-12 text-lg"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Not registered yet?{' '}
              <Link to="/organization/register" className="text-primary font-bold hover:underline">
                Register here
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
              Individual Customer Login →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
